import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import { datadogRum } from "@datadog/browser-rum";

import * as API from "../../../../api/billing";
import Modal from "../../../reusable/reactModal";
import { FlexPaymentModalProps } from "../lib/types";
import {
  StyledButton,
  StyledButtonContainer,
  StyledModalBody,
  StyledModalContents,
  StyledModalSubHeader,
  StyledModalText,
} from "../lib/styles";
import styled from "styled-components";
import { redesignTheme } from "../../../theme/theme";

const StyledInlineModalText = styled(StyledModalText)`
  display: inline;
  padding-right: ${redesignTheme.spacing[1.5]};
`;

const StyledBoldModalText = styled(StyledInlineModalText)`
  font-weight: ${redesignTheme.fontWeight.semibold};
`;

const FlexPaymentModal = ({
  amount,
  closeModal,
  companyId,
  fetchData,
  isModifying,
  modal,
  rewardBalance,
  setLoading,
  setSuccessMessage,
  updateToastMessage,
}: FlexPaymentModalProps): React.ReactElement => {
  // NOTE: refillThreshold is 50% of amount selected
  const refillThreshold = parseFloat(amount) * 0.5;
  // NOTE: reward balance format is "$1,225.60" so we need to remove the $ and , to compare
  const formattedRewardBalance = parseFloat(rewardBalance.replace(/[$,]/g, ""));
  const isBelowThreshold = formattedRewardBalance < refillThreshold;

  const handleClick = async (): Promise<void> => {
    setLoading(true);
    closeModal();

    try {
      const prepayAmountInCents = parseInt(amount, 10) * 100;
      const data = {
        company: {
          billing_information_attributes: {
            rewards_prepay_amount: prepayAmountInCents,
          },
        },
      };

      if (isModifying) {
        await API.putModifyPrepay(companyId, data);
      } else {
        await API.postSetupPrepay(data);
      }

      if (!isModifying) {
        window.amplitude?.track("Billing Settings: Setup Flex Plan (success)");
      }

      setSuccessMessage(
        i18n.translate("admin_pages.settings.billing.plan_change_success")
      );
      fetchData();
    } catch (error) {
      datadogRum.addError(error);
      if (!isModifying) {
        window.amplitude?.track("Billing Settings: Setup Flex Plan (fail)");
      }

      setLoading(false);

      updateToastMessage({
        isOpen: true,
        message: isModifying
          ? i18n.translate("admin_pages.settings.billing.modify_flex_error")
          : i18n.translate("admin_pages.settings.billing.setup_flex_error"),
        type: "error",
      });
    }
  };

  return (
    <Modal
      ariaLabel="payment modal"
      close={closeModal}
      showDialog={modal === "flexPayment"}
      testId="flex-payment-modal"
      title={i18n.translate("admin_pages.settings.billing.modify_flex_plan")}
    >
      <StyledModalBody>
        <StyledModalContents>
          <StyledModalSubHeader>
            {i18n.translate("admin_pages.settings.billing.review_plan_header")}
          </StyledModalSubHeader>
          {isBelowThreshold ? (
            <div data-testid="payment-due-text">
              <StyledInlineModalText>
                {i18n.translate(
                  "admin_pages.settings.billing.review_plan_description_payment",
                  { amount: `$${parseInt(amount).toLocaleString()}` }
                )}
              </StyledInlineModalText>
              <StyledBoldModalText>
                {i18n.translate(
                  "admin_pages.settings.billing.review_plan_amount_due",
                  {
                    amountWithFee: `$${(
                      parseFloat(amount) * 1.05
                    ).toLocaleString()}`,
                  }
                )}
              </StyledBoldModalText>
            </div>
          ) : (
            <div data-testid="no-payment-text">
              <StyledInlineModalText>
                {i18n.translate(
                  "admin_pages.settings.billing.review_plan_description_no_payment",
                  {
                    refillThreshold: `$${refillThreshold.toLocaleString()}`,
                    rewardBalance,
                  }
                )}
              </StyledInlineModalText>
              <StyledBoldModalText>
                {i18n.translate(
                  "admin_pages.settings.billing.review_plan_no_payment_due"
                )}
              </StyledBoldModalText>
            </div>
          )}
        </StyledModalContents>
        <StyledButtonContainer>
          <StyledButton
            onClick={closeModal}
            size="small"
            testId="cancel-button"
            variant="secondary"
          >
            {i18n.translate("forms.cancel")}
          </StyledButton>
          <StyledButton
            onClick={handleClick}
            size="small"
            testId={isBelowThreshold ? "agree-button" : "finish-button"}
            variant="primary"
          >
            {isBelowThreshold
              ? i18n.translate("forms.agree_pay")
              : i18n.translate("forms.finish")}
          </StyledButton>
        </StyledButtonContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default FlexPaymentModal;
